/**
 * Frequently Bought Together carousel.
 *
 * The scrolling itself is pure CSS (scroll-snap on an overflow container). This
 * only decorates it: arrows that page by one viewport, dots that mirror scroll
 * position, and hiding the whole nav when everything already fits.
 *
 * Consequences worth stating, because they are why it is built this way:
 *  - no measurement loop, no transform bookkeeping, nothing to resync on resize
 *  - swipe, trackpad, keyboard and screen-reader scrolling all work untouched
 *  - with JS off or broken, the carousel is still a scrollable, usable list
 *
 * Scroll is read in a rAF-throttled passive listener so it never blocks the
 * compositor, and page count is derived from real geometry rather than a
 * hardcoded slides-per-view that would have to duplicate the CSS breakpoints.
 */
(function () {
  if (customElements.get('fbt-carousel')) return;

  customElements.define(
    'fbt-carousel',
    class FbtCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-viewport]');
        this.track = this.querySelector('[data-track]');
        this.nav = this.querySelector('[data-nav]');
        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        this.dotsEl = this.querySelector('[data-dots]');
        this.slides = Array.prototype.slice.call(this.querySelectorAll('.fbt__slide'));

        if (!this.viewport || !this.track || !this.slides.length) return;

        this._onScroll = this._throttled(this._sync.bind(this));
        this.viewport.addEventListener('scroll', this._onScroll, { passive: true });

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._page(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._page(1));

        if (window.ResizeObserver) {
          this._ro = new ResizeObserver(this._throttled(this._build.bind(this)));
          this._ro.observe(this.viewport);
        }

        this._build();
      }

      disconnectedCallback() {
        if (this.viewport && this._onScroll) {
          this.viewport.removeEventListener('scroll', this._onScroll);
        }
        if (this._ro) {
          this._ro.disconnect();
          this._ro = null;
        }
      }

      /** Coalesce bursts of scroll/resize into one read per frame. */
      _throttled(fn) {
        var scheduled = false;
        return function () {
          if (scheduled) return;
          scheduled = true;
          window.requestAnimationFrame(function () {
            scheduled = false;
            fn();
          });
        };
      }

      /** True once the content is wider than the viewport. */
      get _overflowing() {
        return this.viewport.scrollWidth - this.viewport.clientWidth > 1;
      }

      _pageCount() {
        if (!this._overflowing) return 1;
        return Math.ceil(this.viewport.scrollWidth / this.viewport.clientWidth);
      }

      _currentPage() {
        return Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      }

      _build() {
        if (!this.nav) return;

        /* Everything fits — a carousel with nothing to scroll is just noise. */
        if (!this._overflowing) {
          this.nav.hidden = true;
          return;
        }
        this.nav.hidden = false;

        var pages = this._pageCount();
        if (this.dotsEl && this.dotsEl.childElementCount !== pages) {
          this.dotsEl.innerHTML = '';
          var self = this;
          for (var i = 0; i < pages; i++) {
            (function (index) {
              var dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'fbt__dot';
              dot.setAttribute('aria-label', 'Go to slide group ' + (index + 1) + ' of ' + pages);
              dot.addEventListener('click', function () {
                self.viewport.scrollTo({
                  left: index * self.viewport.clientWidth,
                  behavior: 'smooth',
                });
              });
              self.dotsEl.appendChild(dot);
            })(i);
          }
        }

        this._sync();
      }

      _page(direction) {
        this.viewport.scrollBy({
          left: direction * this.viewport.clientWidth,
          behavior: 'smooth',
        });
      }

      _sync() {
        if (!this.nav || this.nav.hidden) return;

        var max = this.viewport.scrollWidth - this.viewport.clientWidth;
        var left = this.viewport.scrollLeft;

        if (this.prevBtn) this.prevBtn.disabled = left <= 1;
        if (this.nextBtn) this.nextBtn.disabled = left >= max - 1;

        if (!this.dotsEl) return;
        var current = this._currentPage();
        var dots = this.dotsEl.children;
        for (var i = 0; i < dots.length; i++) {
          var active = i === current;
          dots[i].classList.toggle('is-active', active);
          dots[i].setAttribute('aria-current', active ? 'true' : 'false');
        }
      }
    },
  );
})();
